import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "preview", "dropzone"]

  connect() {
    this.boundPreventDefaults = this.preventDefaults.bind(this)
    this.boundHighlight = this.highlight.bind(this)
    this.boundUnhighlight = this.unhighlight.bind(this)
    this.boundDrop = this.handleDrop.bind(this)
    this.boundSubmitEnd = this.handleSubmitEnd.bind(this)
    this.form = this.element.closest("form")

    this.reset()
    this.setupDragAndDrop()
    this.setupTurboEvents()
  }

  setupDragAndDrop() {
    if (!this.hasDropzoneTarget) return

    const dropzone = this.dropzoneTarget
    this.dropzone = dropzone
    this.body = document.body

    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.boundPreventDefaults, false)
      this.body.addEventListener(eventName, this.boundPreventDefaults, false)
    })
    ;['dragenter', 'dragover'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.boundHighlight, false)
    })
    ;['dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.boundUnhighlight, false)
    })
    dropzone.addEventListener('drop', this.boundDrop, false)
  }

  setupTurboEvents() {
    this.form?.addEventListener("turbo:submit-end", this.boundSubmitEnd)
  }

  disconnect() {
    if (this.dropzone) {
      ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        this.dropzone.removeEventListener(eventName, this.boundPreventDefaults, false)
      })
      ;['dragenter', 'dragover'].forEach(eventName => {
        this.dropzone.removeEventListener(eventName, this.boundHighlight, false)
      })
      ;['dragleave', 'drop'].forEach(eventName => {
        this.dropzone.removeEventListener(eventName, this.boundUnhighlight, false)
      })
      this.dropzone.removeEventListener('drop', this.boundDrop, false)
    }

    if (this.body) {
      ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        this.body.removeEventListener(eventName, this.boundPreventDefaults, false)
      })
    }

    this.form?.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
    this.dropzone?.classList.remove('drag-over')
    this.dropzone = null
    this.body = null
    this.form = null
  }

  reset() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    if (this.hasPreviewTarget) this.previewTarget.innerHTML = '<p class="text-muted">No files selected</p>'
    this.dropzone?.classList.remove('drag-over')
  }

  handleSubmitEnd(event) {
    if (event.detail?.success) {
      this.reset()
    }
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight(e) {
    this.dropzoneTarget.classList.add('drag-over')
  }

  unhighlight(e) {
    this.dropzoneTarget.classList.remove('drag-over')
  }

  handleDrop(e) {
    const dt = e.dataTransfer
    this.handleFiles(dt?.files, this.inputTarget.multiple)
  }

  selectFiles(e) {
    const files = e.target.files
    this.handleFiles(files, false)
  }

  handleFiles(files, accumulate = false) {
    if (!this.hasInputTarget) return

    const fileInput = this.inputTarget
    const incomingFiles = Array.from(files || [])
    const selectedFiles = fileInput.multiple && accumulate
      ? [...Array.from(fileInput.files), ...incomingFiles]
      : incomingFiles
    const filesToAssign = fileInput.multiple ? selectedFiles : selectedFiles.slice(0, 1)
    const dt = new DataTransfer()
    filesToAssign.forEach(file => dt.items.add(file))

    fileInput.files = dt.files
    this.updatePreview()
  }

  updatePreview() {
    const files = this.inputTarget.files
    const preview = this.previewTarget
    if (!files || files.length === 0) {
      preview.innerHTML = '<p class="text-muted">No files selected</p>'
      return
    }
    preview.innerHTML = ''
    Array.from(files).forEach((file, index) => {
      const fileItem = document.createElement('div')
      fileItem.className = 'file-item d-flex align-items-center justify-content-between mb-2 p-2 border rounded'
      const fileInfo = document.createElement('div')
      fileInfo.className = 'file-info d-flex align-items-center flex-grow-1'
      const icon = document.createElement('i')
      icon.className = 'bi bi-file-earmark'
      const fileName = document.createElement('span')
      fileName.className = 'file-name fw-medium ms-2'
      fileName.textContent = file.name
      const fileSize = document.createElement('small')
      fileSize.className = 'text-muted ms-2'
      fileSize.textContent = `(${this.formatFileSize(file.size)})`
      fileInfo.append(icon, fileName, fileSize)

      const removeButton = document.createElement('button')
      removeButton.type = 'button'
      removeButton.className = 'btn btn-sm btn-outline-danger'
      removeButton.dataset.action = 'click->file-upload#removeFile'
      removeButton.dataset.fileIndex = index
      removeButton.setAttribute('aria-label', `Remove ${file.name}`)
      removeButton.innerHTML = '<i class="bi bi-x"></i>'

      fileItem.append(fileInfo, removeButton)
      preview.appendChild(fileItem)
    })
  }

  removeFile(e) {
    const fileIndex = parseInt(e.currentTarget.dataset.fileIndex, 10)
    const fileInput = this.inputTarget
    const dt = new DataTransfer()
    for (let i = 0; i < fileInput.files.length; i++) {
      if (i !== fileIndex) {
        dt.items.add(fileInput.files[i])
      }
    }
    fileInput.files = dt.files
    this.updatePreview()
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
}
